import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useContext, useEffect, Suspense } from "react";
import UserContext from "common/Context/UserContext";
import Dashboard from "pages/Dashboard";
import Admin from "pages/Admin/Admin";
import INFRAMARK_CONSTANTS from "common/Constants/inframark-constants";
import Customers from "pages/Admin/Customer";
import { ConfirmResetPassword } from "pages/Authentication";
import Administration from "pages/Home/Administration";
import BaseComponent from "common/Components/BaseComponent";
import CustomerAccessRevoked from "common/Components/CustomerAccessRevoked";
import { CloudView } from "common/Constants/route-components";
import PlatformUserAccess from "pages/PlatformUserAccess";
import { isPlatformAdminProfile } from "common/Utilities/Role.utils";
import { isPlatformAdminHomePath } from "common/Utilities/CustomerSelection.utils";
import EffectiveAccessRouteGuard from "./EffectiveAccessRouteGuard";
import DefaultLandingRedirect from "./DefaultLandingRedirect";
import {
  canAccessPageForProfile,
  hasDashboardPageAccess,
  hasSwitchableCustomerAccess,
} from "common/Utilities/EffectiveAccess.utils";
import { EFFECTIVE_ACCESS_PAGE_KEY } from "common/Constants/EffectiveAccess.constants";
import NewLayout from "common/Components/Layout/NewLayout";

const NavigateToRoot = () => <Navigate to="/" replace />;

const isPlatformAdminBasePath = (pathname = "") =>
  isPlatformAdminHomePath(pathname);

const HomeRoutes = () => {
  const { userProfile } = useContext(UserContext);
  const { pathname } = useLocation();
  const profile = userProfile?.profile ?? {};

  useEffect(() => {
    const currentPath = window.location.pathname;
    if (currentPath.includes("undefined")) {
      const segments = currentPath
        .split("/")
        .filter((segment) => segment && segment !== "undefined");
      const cleanPath = segments.length ? `/${segments.join("/")}` : "/";
      window.history.replaceState(null, null, cleanPath);
    }
  }, []);

  const hasSessionWarning = Boolean(profile?.sessionWarning);
  const isPlatformAdmin = isPlatformAdminProfile(profile);
  const shouldShowRevokedAccessScreen =
    hasSessionWarning && !(isPlatformAdmin && isPlatformAdminBasePath(pathname));

  const canAccessDataManagement = canAccessPageForProfile(
    profile,
    EFFECTIVE_ACCESS_PAGE_KEY.DATA_MANAGEMENT
  );
  const canAccessUserManagement = canAccessPageForProfile(
    profile,
    EFFECTIVE_ACCESS_PAGE_KEY.USER_MANAGEMENT
  );
  const canAccessCustomers = canAccessPageForProfile(
    profile,
    EFFECTIVE_ACCESS_PAGE_KEY.CUSTOMER
  );
  const canAccessPlatformUserAccess = canAccessPageForProfile(
    profile,
    EFFECTIVE_ACCESS_PAGE_KEY.USER_ACCESS_CONTROL
  );
  const canAccessDashboard =
    Boolean(profile?.effectiveAccess) && hasDashboardPageAccess(profile);

  const revokedAccessHint = hasSwitchableCustomerAccess(profile)
    ? undefined
    : "";

  return (
    <EffectiveAccessRouteGuard>
      <Routes>
        <Route element={<NewLayout />}>
          {shouldShowRevokedAccessScreen ? (
            <Route
              path="*"
              element={
                <CustomerAccessRevoked
                  withLayout={false}
                  message={profile.sessionWarning}
                  title={profile.sessionWarningTitle}
                  hint={revokedAccessHint}
                />
              }
            />
          ) : (
            <>
          <Route path="/" element={<DefaultLandingRedirect />} />
          <Route path="/home" element={<NavigateToRoot />} />
          <Route
            path={INFRAMARK_CONSTANTS.navigationRoutes.userManagement}
            element={
              canAccessUserManagement ? <Admin /> : <NavigateToRoot />
            }
          />
          <Route
            path="/dashboard"
            element={
              canAccessDashboard ? <Dashboard /> : <NavigateToRoot />
            }
          />
          <Route
            path="/customers"
            element={
              canAccessCustomers ? <Customers /> : <NavigateToRoot />
            }
          />
          <Route
            path="/user-access"
            element={
              canAccessPlatformUserAccess ? (
                <PlatformUserAccess />
              ) : (
                <NavigateToRoot />
              )
            }
          />
          <Route
            path={INFRAMARK_CONSTANTS?.navigationRoutes?.dataManagement}
            element={
              canAccessDataManagement ? (
                <Administration />
              ) : (
                <NavigateToRoot />
              )
            }
          />
          <Route
            path={`${INFRAMARK_CONSTANTS?.navigationRoutes?.dataManagement}/*`}
            element={
              canAccessDataManagement ? (
                <Administration />
              ) : (
                <NavigateToRoot />
              )
            }
          />
          <Route
            path={INFRAMARK_CONSTANTS?.navigationRoutes?.cloudHierarchy}
            element={
              canAccessDashboard ? (
                <BaseComponent>
                  <Suspense fallback={<div>Loading...</div>}>
                    <CloudView />
                  </Suspense>
                </BaseComponent>
              ) : (
                <NavigateToRoot />
              )
            }
          />
          <Route path="/access_denied" element={<NavigateToRoot />} />
          <Route path="*" element={<NavigateToRoot />} />
            </>
          )}
        </Route>
        <Route path="/confirm-reset-password" element={<ConfirmResetPassword />} />
        <Route path="/signup/invite" element={<Navigate to="/" replace />} />
      </Routes>
    </EffectiveAccessRouteGuard>
  );
};

export default HomeRoutes;
